import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import DateOfBirthInput from '../components/DateOfBirthInput';
import { validatePatientForGearOrder } from '../util/apiUtils';
import workflowUtil from '../util/workflowUtil';
import AlertMessage from '../AlertMessage';
import { ReCAPTCHA } from 'react-google-recaptcha';
import PrcRequisitionSearch from '../requisition-search/PrcRequisitionSearch';

export default function PITLLandingPage({ userInfo, setLoading }) {
	const history = useHistory();
	const queryString = useLocation()?.search;
	const { id: gearOrderId } = useParams();
	const productIds = new URLSearchParams(queryString).get('products')?.split(',');

	const [errorMessage, setErrorMessage] = useState(null);
	const [showError, setShowError] = useState(false);

	// Redirect into /order when we have products
	useEffect(() => {
		if (productIds?.length && userInfo) {
			// TODO: Verify product Ids are valid
			history.push({
				pathname: '/order/connect',
				search: queryString
			});
		}
	}, [history, productIds, queryString, userInfo, gearOrderId]);

	let searchComponent;
	if (gearOrderId) {
		searchComponent = (
			<ZipCodeSearch
				userInfo={userInfo}
				setLoading={setLoading}
				setShowError={setShowError}
				setErrorMessage={setErrorMessage}
			/>
		);
	} else {
		searchComponent = <PrcRequisitionSearch userInfo={userInfo} setLoading={setLoading} />;
	}

	return (
		<div style={{ padding: '2em' }}>
			<AlertMessage display={showError === true} type="danger">
				{typeof errorMessage === 'string' && <strong>{errorMessage}</strong>}
			</AlertMessage>
			<div className="container">
				<div className="row">
					<div className="col-md-8 col-sm-7">
						<h1>Welcome Back!</h1>
						<p>Please verify your information to get started.</p>
						{searchComponent}
					</div>
				</div>
			</div>
		</div>
	);
}

const ZipCodeSearch = ({ userInfo, setLoading, setShowError, setErrorMessage }) => {
	const history = useHistory();
	const { id: gearOrderId } = useParams();
	const [validated, setValidated] = useState(null);
	const [form, setForm] = useState({ userZip: '', userDob: '' });
	const [grecaptchaObject, setGrecaptchaObject] = useState(window.grecaptchaObject);
	const [captchaValue, setCaptchaValue] = useState(null);
	const [scriptCount, setScriptCount] = useState(0);

	/**
	 * Reset the ReCaptcha
	 */
	const resetReCaptcha = () => {
		if (grecaptchaObject && grecaptchaObject.reset) {
			grecaptchaObject.reset();
		}
		setCaptchaValue(null);
	};

	/**
	 * Attempt to store the window.grecaptcha value in state.
	 * If the value is not available, try again after 500 ms
	 */
	const loadGrecaptchaObject = () => {
		// Setting grecaptchaObject doesn't always force a re-render because of how it is a reference to window.grecaptcha
		// The scriptCount value is to make sure state is refreshed and a re-render happens each time this method is called
		setScriptCount((prev) => prev + 1);
		const newObj = window.grecaptcha;

		if (!newObj || !newObj.reset) {
			setTimeout(() => {
				loadGrecaptchaObject();
			}, 500);
		} else {
			setGrecaptchaObject(newObj);
		}
	};

	/**
	 * Load the Google ReCaptcha script, which will create the window.grecaptcha object
	 */
	const loadRecaptchaScript = () => {
		const script = document.createElement('script');
		script.src = '//www.google.com/recaptcha/api.js';
		script.type = 'text/javascript';
		document.head.appendChild(script);
		// script.onload = () => {
		loadGrecaptchaObject();
		// };
	};

	/**
	 * Load the Google ReCaptcha script and object when the component mounts.
	 */
	useEffect(() => {
		if (!window.grecaptcha) {
			loadRecaptchaScript();
		} else {
			loadGrecaptchaObject();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleRecaptchaChange = (value) => {
		// null is passed in when the recaptcha expires. Change that to 'false' so we know the recaptcha has been attempted.
		setCaptchaValue(value || false);
	};

	const dobHandler = (date) => {
		setForm((prevState) => ({
			...prevState,
			userDob: date
		}));
	};

	const zipHandler = (event) => {
		const zipCode = event?.target?.value ?? '';
		setForm((prevState) => ({
			...prevState,
			userZip: zipCode
		}));
	};

	const handleSubmit = (event) => {
		event.preventDefault(); // Fix for the weird page reload flushing state and resetting progress
		setShowError(false);
		setErrorMessage(null);

		if (!captchaValue) {
			setCaptchaValue(false);
			return;
		}

		setLoading(true);

		validatePatientForGearOrder(gearOrderId, form.userDob, form.userZip, captchaValue)
			.then((gearOrder) => {
				setValidated(true);

				let nextStep = 'confirm';
				if (gearOrder?.workflow?.steps) {
					// find the first applicable step in the order's workflow
					let firstIncompleteStep = gearOrder.workflow.steps.find((step, index) => {
						return step && !step.completed && workflowUtil.isStepValidForUser(step, userInfo, gearOrder);
					});
					const firstStepMetaData = workflowUtil.getWorkflowMetaDataById((firstIncompleteStep || {}).id);
					if (firstStepMetaData && firstStepMetaData.route) {
						nextStep = firstStepMetaData.route;
					}
				}

				history.push(`/order/${gearOrderId}/${nextStep}?dateOfBirth=${form.userDob}`);
			})
			.catch((e) => {
				if (e.response && e.response.status === 401) {
					setValidated(false);
				} else {
					setShowError(true);
					setErrorMessage(
						'An unexpected error has occurred. Please try again or call us at 800-522-4762 for assistance.'
					);
				}
				// Scroll up so the error is visible
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setLoading(false);
				resetReCaptcha();
			});
	};

	return (
		<form autoComplete="off" onSubmit={handleSubmit}>
			<div className="row space-bottom">
				<div className="form-group col-5 col-sm-4">
					<div>
						<label className="control-label" htmlFor="patient-zip">
							Zip Code<span className="text-danger">*</span>
						</label>
						<input
							className="form-control"
							id="patient-zip"
							maxLength="5"
							type="text"
							onChange={zipHandler}
							autoComplete="new-password"
							required={true}
						></input>
					</div>
				</div>
				<div className="form-group col-7 col-sm-4" style={{ zIndex: '10' }}>
					<div>
						<label className="control-label" htmlFor="patient-dateOfBirth">
							Date Of Birth<span className="text-danger">*</span>
						</label>
						<DateOfBirthInput
							onChange={dobHandler}
							value={form.userDob}
							displayAge={false}
							hideInputLables={true}
							required={true}
						/>
					</div>
				</div>
			</div>
			<div className="row space-bottom">
				<div className={'col-12 col-md-6 gear-recaptcha'} style={{ minHeight: '83px' }}>
					<ReCAPTCHA
						grecaptcha={grecaptchaObject}
						sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITEKEY}
						onChange={handleRecaptchaChange}
						style={{ display: 'inline-block', outline: captchaValue === false ? '1px solid red' : '' }}
						scriptcount={scriptCount}
					/>
				</div>
			</div>
			<div className="row">
				<div className="col-12">
					{validated === false ? (
						<p className="text-danger">Validation failed, please check your information for this order.</p>
					) : (
						''
					)}
				</div>
			</div>
			<div className="row">
				<div className="col-sm-10 col-md-6 col-lg-5">
					<button className="btn btn-primary rounded-pill margin-clear btn-block" type="submit">
						<strong>Continue</strong>
					</button>
				</div>
			</div>
		</form>
	);
};
